import { Button, type ButtonProps } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { Stop } from "@/components/icons/customIcons.tsx";
import { ArrowUp, Loader2 } from "lucide-react";
import type { ThreadActivityState } from "../../../types/thread.ts";
import { ARIA_LABELS, UI_CONSTANTS } from "./constants.ts";

interface ActionButtonProps {
  isUploading: boolean;
  isSendingMessage: boolean;
  sendInitialMessageMutationPending: boolean;
  threadActivityState?: ThreadActivityState;
  stopMutationPending: boolean;
  /** Whether there is anything to send — text, or an attachment on its own. */
  canSend: boolean;
  onStopExecution: () => void;
}

export const ActionButton = ({
  isUploading,
  isSendingMessage,
  sendInitialMessageMutationPending,
  threadActivityState,
  stopMutationPending,
  canSend,
  onStopExecution,
}: ActionButtonProps) => {
  const isSendDisabled =
    !canSend || isUploading || isSendingMessage || sendInitialMessageMutationPending;

  const getButtonProps = (): ButtonProps => {
    switch (threadActivityState) {
      case "running":
        return {
          type: "button",
          onClick: onStopExecution,
          disabled: stopMutationPending,
          "aria-label": ARIA_LABELS.STOP_EXECUTION,
          "data-statsig-name": "thread-message-stop",
          children: <Stop className={UI_CONSTANTS.ICON_SIZE} />,
        };

      case "stopPending":
        return {
          type: "button",
          disabled: true,
          "aria-label": ARIA_LABELS.STOP_EXECUTION,
          "data-statsig-name": "thread-message-stop",
          children: <Loader2 className={cn(UI_CONSTANTS.ICON_SIZE, "animate-spin")} />,
        };

      default:
        return {
          type: "submit",
          disabled: isSendDisabled,
          "aria-label": ARIA_LABELS.SEND_MESSAGE,
          "data-statsig-name": "thread-message-send",
          children: isUploading ? (
            <Loader2 className={cn(UI_CONSTANTS.ICON_SIZE, "animate-spin")} />
          ) : (
            <ArrowUp className={UI_CONSTANTS.ICON_SIZE} />
          ),
        };
    }
  };

  const isStopButton = threadActivityState === "running" || threadActivityState === "stopPending";

  return (
    <div
      className={cn("flex flex-row", {
        // Dim until there is something to send. Keyed on content rather than
        // on focus, so the button is brightest exactly when pressing it does
        // something, and stays lit for a composer that has text but has lost
        // focus — which also sidesteps a Safari bug where the blur fires and
        // breaks form submission as you tap the button
        // (https://github.com/facebook/react/issues/2011).
        //
        // The stop button stays fully visible: it is an active action, and it
        // is never in a state where pressing it does nothing.
        "opacity-50": !canSend && !isStopButton,
      })}
    >
      <Button
        variant="primary"
        size="sm"
        className={cn(UI_CONSTANTS.BUTTON_SIZE, "p-0")}
        {...getButtonProps()}
      />
    </div>
  );
};
